import cx from "classnames";
import Link from "next/link";

import getSocialImpactPage from "api/requests/getSocialImpactPage";
import publicConfig from "config/public";
import { Action } from "constants/events";
import { CtaSection, SocialImpactResponse } from "custom-types/Directus";
import { DispensaryV3 } from "custom-types/DispensariesV3";
import { useEventTracker } from "hooks/useEventTracker";
import { createGetServerSideProps } from "utils/createGetServerSideProps";
import { determineOrderMethod } from "utils/determineOrderMethod";
import { getMerchId } from "utils/eventTracking";

import Button from "components/botanic/Button";
import DispensariesCarousel from "components/DispensariesCarousel";
import Image from "components/Image";
import { Sizes } from "components/Image/Image";
import MetaTags from "components/MetaTags";
import NewsSection from "components/NewsSection";
import NoResults from "components/NoResults";
import SectionWrapper from "components/SectionWrapper";
import Video from "components/Video";

const {
  imgix: { publicUrl },
} = publicConfig;

const ImageBlockCTASection = ({ section }: { section: CtaSection }) => (
  <SectionWrapper
    title={section.title}
    subtitle={section.subtitle}
    className="container mt-xxl mb-section"
    link={section.link}
    linkText={section.link_text}
    changeLocation={false}
  >
    <div className="row">
      {section.blocks?.map((block, i) => {
        const blocksCount = section.blocks.length;
        const sizes: Sizes =
          blocksCount === 2 || blocksCount === 4
            ? [512, 735, 362, 490, null, 512]
            : blocksCount === 1
              ? [736, null, null, 992, null, 736]
              : [512, 735, 238, 322, null, 512];
        const className = {
          "md:col-1/2": blocksCount === 2 || blocksCount === 4,
          "md:col-1/3":
            blocksCount === 3 || blocksCount === 5 || blocksCount === 6,
        };
        return (
          <div
            key={`image-block-${i}`}
            className={cx("col mb-lg md:mb-none", className)}
            style={{ height: 290 }}
          >
            <Link
              href={block.link}
              className="block h-full w-full relative carousel-card--gradient"
            >
              {(block.title || block.subtitle) && (
                <div className="text-white absolute p-lg bottom-0 z-10">
                  {block.title && (
                    <h3
                      className="heading--xxl"
                      style={{ color: block.color_text }}
                    >
                      {block.title}
                    </h3>
                  )}
                  {block.subtitle && (
                    <p className="mb-none" style={{ color: block.color_text }}>
                      {block.subtitle}
                    </p>
                  )}
                </div>
              )}
              {block.image && (
                <Image
                  src={block.image}
                  sizes={sizes}
                  background
                  alt={`${block.title} background image`}
                />
              )}
            </Link>
          </div>
        );
      })}
    </div>
  </SectionWrapper>
);

type LandingPageProps = { data: SocialImpactResponse<string, string[]> };

const LandingPage: React.FC<LandingPageProps> = ({ data }) => {
  const { publishEvent } = useEventTracker();

  const trackCardImpression = (slot: number, dispensary: DispensaryV3) => {
    const eventLabel = determineOrderMethod(
      !!dispensary.flags.includes("pickup"),
      !!dispensary.flags.includes("delivery"),
      dispensary.tier,
      dispensary.flags,
    );

    const merchandisingCampaignId = getMerchId(dispensary, "platinumplacement");

    publishEvent({
      action: Action.impression,
      category: "Dispensaries Carousel",
      dispensaryId: dispensary?.id,
      label: `slot #${slot + 1}: ${eventLabel}`,
      // @ts-ignore -- TODO: Fix publishEvent types (https://leafly.atlassian.net/browse/SHH-1133)
      merchandisingCampaignId,
    });
  };

  return (
    <>
      <MetaTags
        title={data.meta_title}
        description={data.meta_description}
        image={`${data.hero_main_image}?w=1200&h=630&fit=crop`}
      />
      <div
        className="relative"
        style={{ backgroundColor: data.hero_background_color }}
      >
        <div className="container pb-section md:pb-none pt-section relative z-10 text-white">
          {data.hero_intro && (
            <span
              className="mb-xxl block"
              style={{ color: data.hero_text_color }}
            >
              {data.hero_intro}
            </span>
          )}
          {data.hero_title && (
            <h1
              className="heading--hero mb-xxl"
              style={{ color: data.hero_text_color }}
            >
              {data.hero_title}
            </h1>
          )}
          {data.hero_description && (
            <p
              className="md:columns-2 mb-xl"
              style={{ color: data.hero_text_color }}
            >
              {data.hero_description}
            </p>
          )}
          {data.hero_main_image && (
            <div
              style={{ height: 450 }}
              className="relative hidden md:block md:-mx-lg lg:mx-none"
            >
              <Image
                src={data.hero_main_image}
                sizes={[736, null, null, 992, null, 736]}
                alt={data.hero_main_image_alt || `${data.hero_title} image`}
                disableLazyLoad
                preload
                background
              />
            </div>
          )}
        </div>
        {data.hero_background_image && (
          <Image
            src={data.hero_background_image}
            sizes={[544, 767, 1023, 1440, null, 767]}
            alt={`${data.hero_title} background image`}
            disableLazyLoad
            preload
            background
          />
        )}
      </div>
      {data.features?.length &&
        data.features.map((feature, i) => (
          <div
            className={cx(
              "max-md:flex-col-reverse container flex flex-col p-none md:flex-row",
              { "md:flex-row-reverse": i % 2 },
            )}
            key={`feature-${i}`}
          >
            <div className="flex-1">
              <div
                className={cx("my-section md:py-section", {
                  "md:pl-section": i % 2,
                  "md:pr-section": !(i % 2),
                })}
              >
                {feature.is_quote && (
                  <span className="block mb-lg" style={{ width: 32 }}>
                    <Image
                      src={`${publicUrl}/web-home/sjei/quote.png`}
                      sizes={[32]}
                      alt="quotation mark"
                    />
                  </span>
                )}
                {feature.title && (
                  <h2 className="heading--hero mb-lg">{feature.title}</h2>
                )}
                {feature.description && (
                  <p className="mb-lg">{feature.description}</p>
                )}
                {feature.button_link && feature.button_text && (
                  <Button href={feature.button_link}>
                    {feature.button_text}
                  </Button>
                )}
              </div>
            </div>
            {feature.image &&
              (feature.attribution_name || feature.attribution_subtext) && (
                <div className="flex-1 -mx-lg lg:mx-none relative">
                  <figure className="max-md:w-[320px] feature-image block relative w-full h-full carousel-card--gradient">
                    {(feature.attribution_name ||
                      feature.attribution_subtext) && (
                      <figcaption className="absolute bottom-0 text-white z-10 p-xl">
                        {feature.attribution_name && (
                          <span className="block heading--hero">
                            {feature.attribution_name}
                          </span>
                        )}
                        {feature.attribution_subtext && (
                          <span className="block">
                            {feature.attribution_subtext}
                          </span>
                        )}
                      </figcaption>
                    )}
                    <Image
                      src={feature.image}
                      sizes={[544, 767, 384, 496, null, 767]}
                      background
                      alt={`${
                        feature.attribution_name || feature.attribution_subtext
                      } background image`}
                    />
                  </figure>
                </div>
              )}
          </div>
        ))}
      {data.tray_type === "dispensaries" && (
        <div className="py-xxl bg-leafly-white">
          <DispensariesCarousel
            title={data.tray_title}
            subtitle={data.tray_subtitle}
            changeLocation={data.tray_location_changer}
            link={data.tray_link}
            linkText={data.tray_link_text}
            isSjei={true}
            page={`${data.slug} landing page`}
            trackCardImpression={trackCardImpression}
            customNoResults={
              data.slug === "social-impact" && (
                <NoResults sectionName="landing-page">
                  <p>
                    We don’t see any Black owned stores in your area, and that’s
                    a problem.
                  </p>
                  <p>
                    As we work on this in our company and in our industry,{" "}
                    <Link
                      href="/news/tags/social-justice"
                      className="no-underline font-bold text-green"
                    >
                      read best-in-class journalism exploring social justice in
                      cannabis.
                    </Link>
                  </p>
                </NoResults>
              )
            }
          />
        </div>
      )}
      {data.image_block_cta_sections?.length && (
        <ImageBlockCTASection section={data.image_block_cta_sections[0]} />
      )}
      {data.video_section_player_script &&
        data.video_section_player_playlist && (
          <SectionWrapper
            title={data.video_section_title}
            subtitle={data.video_section_subtitle}
            link={data.video_section_link}
            linkText={data.video_section_link_text}
            className="container my-xxl"
            changeLocation={false}
          >
            <Video
              playerId="landing-page-video-player"
              playerScript={data.video_section_player_script}
              playlist={data.video_section_player_playlist}
            />
          </SectionWrapper>
        )}
      {data.slug === "social-impact" && (
        <SectionWrapper
          changeLocation={false}
          title="Equity and equality"
          subtitle="They aren’t the same but they have something in common: Each needs greater representation in cannabis."
          className="container my-xxl"
        >
          <div className="row mt-xl">
            <div className="col md:col-1/2 relative pb-[40px] md:pb-0 md:pr-[10%]">
              <h3 className="mb-xs">Equity</h3>
              <p>
                Equity supports people differently depending on need, with an
                eye on proportional representation (race, gender, and more), to
                achieve greater fairness overall.
              </p>
              <div
                role="presentation"
                className={`
                  absolute bottom-[-24px] left-[calc(50%-24px)] md:left-auto md:bottom-auto md:right-[-24px] md:top-[calc(50%-24px)]
                  rounded-full bg-light-grey w-[48px] h-[48px]
                  font-extrabold text-[20px] inline-block text-center leading-[45px]
                `}
              >
                vs
              </div>
            </div>
            <div className="col md:col-1/2 pt-[40px] md:pt-0 md:pl-[10%]">
              <h3 className="mb-xs">Equality</h3>
              <p>
                Equality treats everyone the same regardless of need. An
                important difference in cannabis is that without equity first,
                we will not find equality.
              </p>
            </div>
          </div>
        </SectionWrapper>
      )}
      {data.article_section_title &&
        data.article_section_article_tags?.length && (
          <NewsSection
            title={data.article_section_title}
            subtitle={data.article_section_subtitle}
            link={data.article_section_link}
            linkText={data.article_section_link_text}
            apiTags={data.article_section_article_tags}
            showTags={false}
            page={`${data.slug} landing page`}
            className="bg-leafly-white py-xxl"
          />
        )}
      {data.image_block_cta_sections?.length &&
        data.image_block_cta_sections[1] && (
          <ImageBlockCTASection section={data.image_block_cta_sections[1]} />
        )}
    </>
  );
};

export const getServerSideProps = createGetServerSideProps<LandingPageProps>(
  async () => {
    const data = await getSocialImpactPage();

    if (!data) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        data,
      },
    };
  },
);

export default LandingPage;
